import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaBullhorn,
  FaClock,
  FaCode,
  FaEye,
  FaFileAlt,
  FaFilter,
  FaPlus,
  FaSearch,
  FaChevronDown,
} from "react-icons/fa";
import { fetchCampaignList } from "../../api/campaigns.api";
import { businessPath } from "../../config/businessMenu";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { allowDevMockFallback } from "../../utils/devMockFallback";
import WhatsAppConnectPrompt from "./WhatsAppConnectPrompt";
import DailyCampaignLimitBanner from "./DailyCampaignLimitBanner";

const FILTER_OPTIONS = [
  "All",
  "CSV Broadcast",
  "Scheduled Broadcast",
  "Broadcast",
  "API Broadcast",
];

const MOCK_CAMPAIGNS = [
  {
    _id: "6a06b75b7b9f78d88fd8eb6e",
    campaignName: "FollowupCampaign_15May26",
    campaignType: "CSVBROADCAST",
    campaignstatus: "SENT",
    submitteddata: 74,
    sentCount: 73,
    deliveredCount: 11,
    readCount: 9,
    failedCount: 62,
  },
  {
    _id: "69fb27e3ecf68aefec08e399",
    campaignName: "Cam_12thDropper_06May26",
    campaignType: "CSVBROADCAST",
    campaignstatus: "SENT",
    submitteddata: 77,
    sentCount: 77,
    deliveredCount: 58,
    readCount: 45,
    failedCount: 16,
  },
  {
    _id: "69f9c2d3ecf68aefece5f2de",
    campaignName: "Cam_AllMIX_05May26",
    campaignType: "CSVBROADCAST",
    campaignstatus: "SENT",
    submitteddata: 5924,
    sentCount: 5923,
    deliveredCount: 3677,
    readCount: 2543,
    failedCount: 2049,
  },
];

const FetchingAllCampaign = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { phoneNoId, projectLoading, needsWhatsAppSetup } = useProjectPhone();
  const filterRef = useRef(null);
  const createRef = useRef(null);

  const [campaigns, setCampaigns] = useState([]);
  const [search, setSearch] = useState("");
  const [tabFilter, setTabFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalCsv, setTotalCsv] = useState(0);
  const [totalApi, setTotalApi] = useState(0);
  const [totalScheduled, setTotalScheduled] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchCampaigns = useCallback(
    async (pageNum, searchVal, filterVal) => {
      if (!phoneNoId) {
        setCampaigns([]);
        setTotalPages(1);
        setTotalRecords(0);
        setTotalCsv(0);
        setTotalApi(0);
        setTotalScheduled(0);
        setLoading(false);
        setError("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await fetchCampaignList(phoneNoId, {
          page: pageNum,
          search: searchVal,
          tabFilter: filterVal,
          limit,
        });
        setCampaigns(res?.data || []);
        const p = res?.pagination || {};
        setTotalPages(p.totalPages || 1);
        setTotalRecords(p.totalRecords || 0);
        setTotalCsv(p.totalcsvcampaigns || 0);
        setTotalApi(p.totalapicampaigns || 0);
        setTotalScheduled(p.totalScheduledcampaigns || 0);
      } catch (err) {
        if (allowDevMockFallback()) {
          setCampaigns(MOCK_CAMPAIGNS);
          setTotalPages(1);
          setTotalRecords(MOCK_CAMPAIGNS.length);
          setTotalCsv(13);
          setTotalApi(0);
          setTotalScheduled(0);
          setError("");
        } else {
          setCampaigns([]);
          setTotalPages(1);
          setTotalRecords(0);
          setTotalCsv(0);
          setTotalApi(0);
          setTotalScheduled(0);
          setError(err.message || "Failed to load campaigns");
        }
      } finally {
        setLoading(false);
      }
    },
    [phoneNoId, limit, projectLoading],
  );

  useEffect(() => {
    if (projectLoading) return;
    fetchCampaigns(page, search, tabFilter);
  }, [fetchCampaigns, page, search, tabFilter, projectLoading]);

  useEffect(() => {
    const onDoc = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
      if (createRef.current && !createRef.current.contains(e.target)) {
        setCreateOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const stats = useMemo(
    () => ({
      total: totalRecords,
      csv: totalCsv,
      api: totalApi,
      scheduled: totalScheduled,
    }),
    [totalRecords, totalCsv, totalApi, totalScheduled],
  );

  const onSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const onFilterSelect = (f) => {
    setTabFilter(f);
    setPage(1);
    setFilterOpen(false);
  };

  const createLinks = [
    {
      label: "Create CSV Campaign",
      path: "campaign",
    },
    {
      label: "Create API Campaign",
      path: "apicampaign",
    },
    {
      label: "Create Broadcast Campaign",
      path: "broadcastCampaign",
    },
  ];

  if (projectLoading) {
    return (
      <div className="campaign-report-page">
        <p className="campaign-report-empty">Loading project…</p>
      </div>
    );
  }

  if (needsWhatsAppSetup) {
    return (
      <div className="campaign-report-page">
        <WhatsAppConnectPrompt
          projectId={projectId}
          title="Connect WhatsApp to view campaigns"
          description="Campaign reports are tied to your linked WhatsApp Business number. Connect your number in project settings first."
        />
      </div>
    );
  }

  return (
    <div className="campaign-report-page">
      <DailyCampaignLimitBanner title="Campaign Report" icon={<FaBullhorn />} />

      <section className="campaign-stats">
        <article className="campaign-stat-card is-total">
          <div className="campaign-stat-icon">
            <FaBullhorn />
          </div>
          <div className="campaign-stat-copy">
            <span>Total Campaign</span>
            <strong>{stats.total}</strong>
          </div>
        </article>
        <article className="campaign-stat-card is-csv">
          <div className="campaign-stat-icon">
            <FaFileAlt />
          </div>
          <div className="campaign-stat-copy">
            <span>CSV Campaign</span>
            <strong>{stats.csv}</strong>
          </div>
        </article>
        <article className="campaign-stat-card is-api">
          <div className="campaign-stat-icon">
            <FaCode />
          </div>
          <div className="campaign-stat-copy">
            <span>API Campaign</span>
            <strong>{stats.api}</strong>
          </div>
        </article>
        <article className="campaign-stat-card is-scheduled">
          <div className="campaign-stat-icon">
            <FaClock />
          </div>
          <div className="campaign-stat-copy">
            <span>Scheduled Campaign</span>
            <strong>{stats.scheduled}</strong>
          </div>
        </article>
      </section>

      <section className="campaign-toolbar">
        <div className="campaign-search-wrap" ref={filterRef}>
          <FaSearch className="campaign-search-icon" />
          <input
            type="search"
            placeholder="Search "
            value={search}
            onChange={onSearchChange}
          />
          <button
            type="button"
            className="campaign-filter-btn"
            onClick={() => setFilterOpen((o) => !o)}
            aria-label="Filter"
          >
            <FaFilter />
          </button>
          {filterOpen && (
            <div className="campaign-filter-menu">
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`campaign-filter-opt ${
                    tabFilter === opt ? "active" : ""
                  }`}
                  onClick={() => onFilterSelect(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="campaign-create-wrap" ref={createRef}>
          <button
            type="button"
            className="campaign-create-btn"
            onClick={() => setCreateOpen((o) => !o)}
          >
            <FaPlus />
            Create Campaign
            <FaChevronDown />
          </button>
          {createOpen && (
            <div className="campaign-create-menu">
              {createLinks.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() =>
                    navigate(businessPath(projectId, item.path))
                  }
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="campaign-table-section">
        {loading && (
          <p className="campaign-msg campaign-msg-info">
            Loading campaign details...
          </p>
        )}
        {error && <p className="campaign-msg campaign-msg-error">{error}</p>}

        {!loading && !error && campaigns.length === 0 && (
          <p className="campaign-msg">No campaigns found.</p>
        )}

        {!loading && campaigns.length > 0 && (
          <div className="campaign-table-wrap">
            <table className="campaign-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Campaign Name</th>
                  <th>Campaign Type</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th className="biz-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((row, idx) => (
                  <tr key={row._id}>
                    <td>{(page - 1) * limit + idx + 1}</td>
                    <td title={row.campaignName}>
                      {row.campaignName?.length > 20
                        ? `${row.campaignName.slice(0, 20)}...`
                        : row.campaignName || "N/A"}
                    </td>
                    <td>{row.campaignType || "N/A"}</td>
                    <td>{row.submitteddata ?? row.audienceCount ?? "0"}</td>
                    <td>
                      <div className="campaign-status-pills">
                        <span className="campaign-pill campaign-pill-complete">
                          {row.campaignstatus === "SENT" ? "Complete" : "Pending"}
                        </span>
                        <span className="campaign-pill campaign-pill-sent">
                          Sent {row.sentCount ?? 0}
                        </span>
                        <span className="campaign-pill campaign-pill-delivered">
                          Delivered {row.deliveredCount ?? 0}
                        </span>
                        <span className="campaign-pill campaign-pill-read">
                          Read {row.readCount ?? 0}
                        </span>
                        <span className="campaign-pill campaign-pill-failed">
                          Failed {row.failedCount ?? 0}
                        </span>
                      </div>
                    </td>
                    <td className="biz-col-actions">
                      <div className="biz-table-actions">
                        <Link
                          to={businessPath(projectId, `campaign/${row._id}`)}
                          className="biz-btn-outline biz-btn-sm campaign-view-btn"
                        >
                          <FaEye />
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="campaign-pagination">
          <div className="campaign-pagination-left">
            <label htmlFor="campaign-page-size">Show:</label>
            <select
              id="campaign-page-size"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10 / page</option>
              <option value={100}>100 / page</option>
              <option value={200}>200 / page</option>
              <option value={500}>500 / page</option>
              <option value={1000}>1000 / page</option>
            </select>
          </div>
          <div className="campaign-pagination-right">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FetchingAllCampaign;
